import { create } from 'zustand';
import type { Node, Connection, Message, PostIt, Report } from '../types';

interface CoreState {
  nodes: Node[];
  connections: Connection[];
  messages: Message[];
  postIts: PostIt[];
  reports: Report[];
  input: string;
  exchangesWithoutNodes: number;

  setNodes: (nodes: Node[] | ((prev: Node[]) => Node[])) => void;
  setConnections: (connections: Connection[] | ((prev: Connection[]) => Connection[])) => void;
  setMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void;
  setPostIts: (postIts: PostIt[] | ((prev: PostIt[]) => PostIt[])) => void;
  setReports: (reports: Report[] | ((prev: Report[]) => Report[])) => void;
  setInput: (input: string) => void;
  setExchangesWithoutNodes: (n: number | ((prev: number) => number)) => void;
}

type SetterArg<T> = T | ((prev: T) => T);

const resolveUpdate = <T>(arg: SetterArg<T>, prev: T): T =>
  typeof arg === 'function' ? (arg as (prev: T) => T)(prev) : arg;

export const useCoreStore = create<CoreState>()((set) => ({
  nodes: [],
  connections: [],
  messages: [],
  postIts: [],
  reports: [],
  input: '',
  exchangesWithoutNodes: 0,

  setNodes: (arg) => set((s) => ({ nodes: resolveUpdate(arg, s.nodes) })),
  setConnections: (arg) => set((s) => ({ connections: resolveUpdate(arg, s.connections) })),
  setMessages: (arg) => set((s) => ({ messages: resolveUpdate(arg, s.messages) })),
  setPostIts: (arg) => set((s) => ({ postIts: resolveUpdate(arg, s.postIts) })),
  setReports: (arg) => set((s) => ({ reports: resolveUpdate(arg, s.reports) })),
  setInput: (input) => set({ input }),
  setExchangesWithoutNodes: (arg) =>
    set((s) => ({ exchangesWithoutNodes: resolveUpdate(arg, s.exchangesWithoutNodes) })),
}));
